import { Directive, ElementRef, EventEmitter, HostListener, Output } from '@angular/core';
import { invalidOnuTextChars, stripInvalidOnuText } from './onu-text.util';

/**
 * Campo de texto que no deja escribir ni pegar caracteres que SmartOLT rechaza.
 * Avisa con `invalidChars` qué caracteres se bloquearon y con `validInput` cuando el usuario
 * vuelve a escribir algo correcto.
 */
@Directive({
  selector: 'input[appOnuText]',
  standalone: true,
})
export class OnuTextInputDirective {
  @Output() invalidChars = new EventEmitter<string[]>();
  @Output() validInput = new EventEmitter<void>();

  constructor(private readonly el: ElementRef<HTMLInputElement>) {}

  // al escribir o pegar: se cancela la inserción si trae algo no permitido
  @HostListener('beforeinput', ['$event'])
  onBeforeInput(event: InputEvent) {
    if (event.inputType.startsWith('delete') || event.isComposing) return;
    const text = event.data ?? event.dataTransfer?.getData('text') ?? '';
    const bad = invalidOnuTextChars(text);
    if (bad.length) {
      event.preventDefault();
      // en inserciones de varios caracteres (pegar, dictado, autocorrección) entra la parte válida
      const clean = stripInvalidOnuText(text);
      if (clean) this.insert(clean);
      // el aviso va después: insertar dispara 'input', que limpia el error anterior
      this.invalidChars.emit(bad);
    }
  }

  // red de seguridad: autocompletado del navegador, teclados con composición, arrastrar y soltar
  @HostListener('input')
  onInput() {
    const input = this.el.nativeElement;
    const bad = invalidOnuTextChars(input.value);
    if (!bad.length) {
      this.validInput.emit();
      return;
    }
    const caret =
      (input.selectionStart ?? input.value.length) -
      (input.value.length - stripInvalidOnuText(input.value).length);
    input.value = stripInvalidOnuText(input.value);
    input.setSelectionRange(Math.max(0, caret), Math.max(0, caret));
    // se avisa a ngModel del valor ya limpio
    input.dispatchEvent(new Event('input', { bubbles: true }));
    this.invalidChars.emit(bad);
  }

  private insert(text: string) {
    const input = this.el.nativeElement;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    input.setRangeText(text, start, end, 'end');
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }
}
